import React, { useState, useEffect, useRef, useCallback } from 'react';
import { TerraDraw, TerraDrawPolygonMode, TerraDrawLineStringMode, TerraDrawSelectMode } from 'terra-draw';
import { TerraDrawGoogleMapsAdapter } from 'terra-draw-google-maps-adapter';
import { 
  MapPin, Square, Ruler, Trash2, Save, 
  RotateCcw, DollarSign, ChevronDown, X
} from 'lucide-react';
import { ZONE_TYPES, getZoneColor, formatSqFt, formatLinearFt, sqMetersToSqFeet, metersToFeet, isLinearZone } from '../../services/measurementApi';

const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;

const SatelliteMeasure = ({ 
  customer,
  initialAddress = '',
  onSave,
  onCancel,
  existingMeasurement = null,
  serviceTypes = [],
  readOnly = false,
  quickMode = false,
  quickModeServiceName = '',
  quickModeMeasurementType = 'sqft',
}) => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const terraDrawRef = useRef(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [terraDrawReady, setTerraDrawReady] = useState(false);
  const [mapCenter, setMapCenter] = useState({ lat: 35.5946, lng: -80.8172 });
  const [mapZoom] = useState(19);

  const [address, setAddress] = useState(initialAddress || customer?.address || '');
  const [addressComponents, setAddressComponents] = useState({
    city: customer?.city || '',
    state: customer?.state || '',
    zipCode: customer?.zipCode || ''
  });

  const [drawingMode, setDrawingMode] = useState(null);
  const [currentZoneType, setCurrentZoneType] = useState('Driveway');
  const [zones, setZones] = useState([]);
  const [selectedZoneIndex, setSelectedZoneIndex] = useState(null);
  const overlaysRef = useRef([]);
  const addressMarkerRef = useRef(null);

  const [showZonePanel, setShowZonePanel] = useState(!quickMode && window.innerWidth >= 768);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const isLinearMode = quickMode && quickModeMeasurementType === 'linear';

  const totalSqFt = zones.reduce((sum, z) => sum + (z.squareFeet || 0), 0);
  const totalLinearFt = zones.reduce((sum, z) => sum + (z.linearFeet || 0), 0);
  const totalEstimate = zones.reduce((sum, z) => sum + (z.calculatedPrice || 0), 0);
  const quickModeTotal = isLinearMode ? totalLinearFt : totalSqFt;

  const calculateZonePrice = useCallback((sqFt, linearFt, serviceType) => {
    if (!serviceType) return 0;
    const rate = serviceType.baseRate || 0;
    const template = serviceType.pricingTemplate;
    if (template === 'per-sqft' && sqFt > 0) return Math.round(sqFt * rate * 100) / 100;
    if (template === 'per-linear-ft' && linearFt > 0) return Math.round(linearFt * rate * 100) / 100;
    if (template === 'flat-rate') return serviceType.defaultPrice || 0;
    return 0;
  }, []);

  const geocodeAddress = useCallback(async (addressString) => {
    if (!window.google?.maps) return;
    const geocoder = new window.google.maps.Geocoder();
    try {
      const result = await new Promise((resolve, reject) => {
        geocoder.geocode({ address: addressString }, (results, status) => {
          if (status === 'OK' && results[0]) resolve(results[0]);
          else reject(new Error('Address not found'));
        });
      });
      const location = result.geometry.location;
      const newCenter = { lat: location.lat(), lng: location.lng() };
      setMapCenter(newCenter);
      mapInstanceRef.current?.setCenter(newCenter);
      mapInstanceRef.current?.setZoom(19);
      if (addressMarkerRef.current) addressMarkerRef.current.setMap(null);
      if (mapInstanceRef.current && window.google) {
        addressMarkerRef.current = new window.google.maps.Marker({
          position: newCenter,
          map: mapInstanceRef.current,
          title: result.formatted_address,
          icon: {
            path: window.google.maps.SymbolPath.CIRCLE,
            scale: 10,
            fillColor: '#ef4444',
            fillOpacity: 1,
            strokeColor: '#ffffff',
            strokeWeight: 3,
          },
          zIndex: 999,
          animation: window.google.maps.Animation.BOUNCE,
        });
        setTimeout(() => {
          if (addressMarkerRef.current) addressMarkerRef.current.setAnimation(null);
        }, 2000);
      }
      const components = result.address_components;
      setAddressComponents({
        city: components.find(c => c.types.includes('locality'))?.long_name || '',
        state: components.find(c => c.types.includes('administrative_area_level_1'))?.short_name || '',
        zipCode: components.find(c => c.types.includes('postal_code'))?.long_name || ''
      });
      setAddress(result.formatted_address);
    } catch (err) {
      setError('Could not find address. Please try a different address.');
    }
  }, []);

  // EFFECT 1: Load the Google Maps script
  useEffect(() => {
    if (window.google?.maps) {
      setMapLoaded(true);
      return;
    }
    const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');
    if (existingScript) {
      existingScript.addEventListener('load', () => setMapLoaded(true));
      return;
    }
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=geometry,places`;
    script.async = true;
    script.defer = true;
    script.onload = () => setMapLoaded(true);
    script.onerror = () => setError('Failed to load Google Maps');
    document.head.appendChild(script);
  }, []);

  // EFFECT 2: Create the map instance and TerraDraw instance (but do NOT call draw.start() here)
  useEffect(() => {
    if (!mapLoaded || !mapRef.current || mapInstanceRef.current) return;

    const isMobile = window.innerWidth < 768;
    const map = new window.google.maps.Map(mapRef.current, {
      center: mapCenter,
      zoom: mapZoom,
      mapTypeId: 'satellite',
      tilt: 0,
      rotateControl: false,
      gestureHandling: 'greedy',
      mapTypeControl: !isMobile,
      mapTypeControlOptions: {
        style: window.google.maps.MapTypeControlStyle.DROPDOWN_MENU,
        mapTypeIds: ['satellite', 'hybrid', 'roadmap'],
        position: window.google.maps.ControlPosition.BOTTOM_LEFT
      },
      streetViewControl: false,
      fullscreenControl: !isMobile,
      fullscreenControlOptions: { position: window.google.maps.ControlPosition.RIGHT_BOTTOM },
      zoomControl: !isMobile,
      zoomControlOptions: { position: window.google.maps.ControlPosition.RIGHT_BOTTOM }
    });

    mapInstanceRef.current = map;
            map.getDiv().id = 'sat-measure-map';

    // Measuring needs a true top-down view: force 45° imagery off and keep it off
    map.setTilt(0);
    map.addListener('tilt_changed', () => {
      if (map.getTilt() !== 0) map.setTilt(0);
    });

    // Build TerraDraw only once the map has a projection (its overlay is initialised)
    window.google.maps.event.addListenerOnce(map, 'projection_changed', () => {
      const adapter = new TerraDrawGoogleMapsAdapter({ map, lib: window.google.maps, coordinatePrecision: 6 });

      // High-contrast, larger points so vertices stay visible on satellite imagery
      const pt = isMobile ? 10 : 6;
      const vertexStyles = {
        coordinatePointColor: '#FFFFFF',
        coordinatePointWidth: pt,
        coordinatePointOutlineColor: '#2563EB',
        coordinatePointOutlineWidth: 3,
        closingPointColor: '#FACC15',
        closingPointWidth: pt + 2,
        closingPointOutlineColor: '#111827',
        closingPointOutlineWidth: 3,
      };
      const polygonStyles = {
        ...vertexStyles,
        fillColor: '#2563EB',
        fillOpacity: 0.25,
        outlineColor: '#FACC15',
        outlineWidth: 3,
      };
      const lineStyles = {
        ...vertexStyles,
        lineStringColor: '#FACC15',
        lineStringWidth: 4,
      };
      terraDrawRef.current = new TerraDraw({
        adapter,
        modes: [
          new TerraDrawPolygonMode({
            pointerDistance: 12,
            showCoordinatePoints: true,
            styles: polygonStyles,
          }),
          new TerraDrawLineStringMode({
            pointerDistance: 12,
            showCoordinatePoints: true,
            styles: lineStyles,
          }),
          new TerraDrawSelectMode(),
        ],
      });
      setTerraDrawReady(true);
    });

    if (address) geocodeAddress(address);
  }, [mapLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

  // EFFECT 3: Start TerraDraw once Effect 2 has created it (after projection_changed)
  useEffect(() => {
    if (!terraDrawReady || !terraDrawRef.current) return;

    // Safe to start here: TerraDraw is only created after the map's projection_changed event
    const startDraw = () => {
      try {
        if (terraDrawRef.current && !terraDrawRef.current.enabled) {
          terraDrawRef.current.start();

          terraDrawRef.current.on('finish', (id) => {
            const snapshot = terraDrawRef.current.getSnapshot();
            const feature = snapshot.find(f => f.id === id);
            if (!feature) return;

            const geomType = feature.geometry.type;
            const coords = feature.geometry.coordinates;
            let squareFeet = 0, linearFeet = 0, perimeterFeet = 0;

            if (geomType === 'Polygon') {
              const path = coords[0].map(([lng, lat]) => new window.google.maps.LatLng(lat, lng));
              squareFeet = sqMetersToSqFeet(window.google.maps.geometry.spherical.computeArea(path));
              perimeterFeet = metersToFeet(window.google.maps.geometry.spherical.computeLength(path));
            } else if (geomType === 'LineString') {
              const path = coords.map(([lng, lat]) => new window.google.maps.LatLng(lat, lng));
              linearFeet = metersToFeet(window.google.maps.geometry.spherical.computeLength(path));
            }

            setZones(prev => {
              const matchingService = serviceTypes.find(st =>
                st.name.toLowerCase().includes(currentZoneType.toLowerCase()) ||
                currentZoneType.toLowerCase().includes(st.name.toLowerCase())
              );
              const newZone = {
                zoneType: currentZoneType,
                name: `${ZONE_TYPES[currentZoneType]?.label || currentZoneType} ${prev.length + 1}`,
                color: getZoneColor(currentZoneType),
                squareFeet: Math.round(squareFeet),
                linearFeet: Math.round(linearFeet),
                perimeterFeet: Math.round(perimeterFeet),
                geoJson: JSON.stringify(feature.geometry),
                shapeType: geomType === 'Polygon' ? 'polygon' : 'polyline',
                serviceTypeId: matchingService?.id || null,
                serviceTypeName: matchingService?.name || null,
                calculatedPrice: calculateZonePrice(squareFeet, linearFeet, matchingService),
                sortOrder: prev.length,
                terraDrawId: id,
              };
              overlaysRef.current.push(id);
              setSelectedZoneIndex(prev.length);
              return [...prev, newZone];
            });

            setDrawingMode(null);
            terraDrawRef.current?.setMode('select');
          });
        }
      } catch (e) {
        console.error('TerraDraw start failed:', e);
        setError('Map drawing tools failed to initialize. Please refresh.');
      }
    };

    startDraw();
  }, [terraDrawReady]); // eslint-disable-line react-hooks/exhaustive-deps

  const startDrawing = (mode) => {
    if (readOnly || !terraDrawRef.current) return;
    setDrawingMode(mode);
    terraDrawRef.current.setMode(mode === 'polygon' ? 'polygon' : 'linestring');
  };

  const cancelDrawing = () => {
    setDrawingMode(null);
    terraDrawRef.current?.setMode('select');
  };

  const deleteZone = (index) => {
    if (readOnly) return;
    const terraDrawId = overlaysRef.current[index];
    if (terraDrawId && terraDrawRef.current) {
      try { terraDrawRef.current.removeFeatures([terraDrawId]); } catch (_) {}
    }
    overlaysRef.current.splice(index, 1);
    setZones(prev => prev.filter((_, i) => i !== index));
    setSelectedZoneIndex(null);
  };

  const clearAllZones = () => {
    if (readOnly) return;
    if (terraDrawRef.current && overlaysRef.current.length > 0) {
      try { terraDrawRef.current.removeFeatures(overlaysRef.current); } catch (_) {}
    }
    overlaysRef.current = [];
    setZones([]);
    setSelectedZoneIndex(null);
  };

  const updateZoneService = (index, serviceTypeId) => {
    const service = serviceTypes.find(st => st.id === serviceTypeId);
    setZones(prev => prev.map((z, i) => i === index ? {
      ...z,
      serviceTypeId,
      serviceTypeName: service?.name || null,
      calculatedPrice: calculateZonePrice(z.squareFeet, z.linearFeet, service)
    } : z));
  };

  const handleSave = async () => {
    if (!customer) { setError('No customer selected'); return; }
    if (zones.length === 0) { setError('Please draw at least one zone'); return; }
    setSaving(true);
    setError(null);
    try {
      const center = mapInstanceRef.current?.getCenter();
      const zoom = mapInstanceRef.current?.getZoom();
      await onSave({
        customerId: customer.id,
        address,
        city: addressComponents.city,
        state: addressComponents.state,
        zipCode: addressComponents.zipCode,
        latitude: center?.lat() || mapCenter.lat,
        longitude: center?.lng() || mapCenter.lng,
        mapZoomLevel: zoom || mapZoom,
        zones: zones.map((z, i) => ({ ...z, sortOrder: i }))
      });
    } catch (err) {
      setError(err.message || 'Failed to save measurement');
    } finally {
      setSaving(false);
    }
  };

  const initAutocomplete = useCallback((input) => {
    if (!input || !window.google?.maps?.places) return;
    const autocomplete = new window.google.maps.places.Autocomplete(input, {
      types: ['address'],
      componentRestrictions: { country: 'us' }
    });
    autocomplete.addListener('place_changed', () => {
      const place = autocomplete.getPlace();
      if (place.formatted_address) geocodeAddress(place.formatted_address);
    });
  }, [geocodeAddress]);

  return (
    <div className="h-full flex flex-col bg-gray-100">
      {/* Header */}
      <div className="bg-white border-b px-3 md:px-4 py-2 md:py-3 flex items-center justify-between">
        <div className="flex items-center gap-2 md:gap-3">
          <div className="p-1.5 md:p-2 bg-blue-100 rounded-lg">
            <MapPin className="w-4 h-4 md:w-5 md:h-5 text-blue-600" />
          </div>
          <div>
            {quickMode ? (
              <>
                <h2 className="font-semibold text-gray-900 text-sm md:text-base">
                  Measuring for: {quickModeServiceName || 'Service'}
                </h2>
                <p className="text-xs md:text-sm text-gray-500">
                  {isLinearMode ? 'Draw lines to measure length' : 'Draw areas to measure square footage'}
                </p>
              </>
            ) : (
              <>
                <h2 className="font-semibold text-gray-900 text-sm md:text-base">SatMeasure™</h2>
                <p className="text-xs md:text-sm text-gray-500 truncate max-w-[120px] md:max-w-none">
                  {customer?.name || 'Property Measurement'}
                </p>
              </>
            )}
          </div>
        </div>

        <div className="flex items-center gap-1 md:gap-2">
          {quickMode && zones.length > 0 && (
            <div className="hidden md:flex items-center gap-2 mr-2 px-3 py-1.5 bg-blue-50 rounded-lg">
              <span className="text-sm font-bold text-blue-700">
                {isLinearMode ? formatLinearFt(quickModeTotal) : formatSqFt(quickModeTotal)}
              </span>
            </div>
          )}
          {!readOnly && (
            <>
              <button
                onClick={clearAllZones}
                disabled={zones.length === 0}
                className="p-1.5 md:px-3 md:py-1.5 text-sm text-gray-600 hover:text-red-600 disabled:opacity-50"
                title="Clear All"
              >
                <RotateCcw size={16} />
                <span className="hidden md:inline ml-1">Clear</span>
              </button>
              <button
                onClick={handleSave}
                disabled={saving || zones.length === 0}
                className="px-3 py-1.5 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-50 flex items-center gap-1 md:gap-2"
              >
                {saving ? (
                  <>
                    <span className="animate-spin">⏳</span>
                    <span className="hidden md:inline">Saving...</span>
                  </>
                ) : (
                  <>
                    <Save size={16} />
                    <span className="hidden md:inline">{quickMode ? 'Use Measurement' : 'Save Measurement'}</span>
                    <span className="md:hidden">{quickMode ? 'Use' : 'Save'}</span>
                  </>
                )}
              </button>
            </>
          )}
          {onCancel && (
            <button onClick={onCancel} className="p-1.5 md:p-2 text-gray-500 hover:text-gray-700">
              <X size={18} className="md:w-5 md:h-5" />
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="mx-4 mt-3 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          {error}
        </div>
      )}

      <div className="flex-1 flex flex-col md:flex-row overflow-hidden">
        <div className="flex-1 relative min-h-[300px] md:min-h-0">
          {/* Address search bar */}
          <div className="absolute top-3 left-3 right-3 z-10">
            <div className="bg-white rounded-lg shadow-lg flex items-center">
              <MapPin size={18} className="ml-3 text-gray-400 flex-shrink-0" />
              <input
                ref={initAutocomplete}
                type="text"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && geocodeAddress(address)}
                placeholder="Enter property address..."
                className="flex-1 px-2 md:px-3 py-2 md:py-2.5 rounded-lg border-0 focus:ring-0 text-sm min-w-0"
                readOnly={readOnly}
              />
              <button
                onClick={() => geocodeAddress(address)}
                className="px-3 md:px-4 py-2 md:py-2.5 bg-gray-100 hover:bg-gray-200 rounded-r-lg text-sm text-gray-600 flex-shrink-0"
              >
                Search
              </button>
            </div>
            {!quickMode && (
              <button
                onClick={() => setShowZonePanel(!showZonePanel)}
                className="md:hidden mt-2 w-full bg-white rounded-lg shadow-lg px-3 py-2 flex items-center justify-between"
              >
                <span className="text-sm font-medium text-gray-700">
                  {totalSqFt > 0 ? formatSqFt(totalSqFt) : '0 sq ft'}
                  {totalEstimate > 0 && (
                    <span className="ml-2 text-green-600">
                      ${totalEstimate.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </span>
                  )}
                </span>
                <ChevronDown size={16} className={`text-gray-400 transition-transform ${showZonePanel ? 'rotate-180' : ''}`} />
              </button>
            )}
          </div>

          {/* Drawing tools */}
          {!readOnly && !drawingMode && (
            <div className="absolute bottom-4 left-2 right-2 md:left-1/2 md:right-auto md:-translate-x-1/2 z-10">
              <div className="bg-white rounded-lg shadow-lg p-2 flex flex-col md:flex-row items-stretch md:items-center gap-2">
                {quickMode ? (
                  <>
                    {zones.length > 0 && (
                      <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 rounded-lg">
                        <span className="text-sm font-bold text-blue-700">
                          Total: {isLinearMode ? formatLinearFt(quickModeTotal) : formatSqFt(quickModeTotal)}
                        </span>
                        <span className="text-xs text-gray-500">
                          ({zones.length} {zones.length === 1 ? 'area' : 'areas'})
                        </span>
                      </div>
                    )}
                    {isLinearMode ? (
                      <button
                        onClick={() => startDrawing('polyline')}
                        className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 flex items-center justify-center gap-2"
                      >
                        <Ruler size={16} />
                        {zones.length > 0 ? 'Draw Another Line' : 'Draw Line'}
                      </button>
                    ) : (
                      <button
                        onClick={() => startDrawing('polygon')}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 flex items-center justify-center gap-2"
                      >
                        <Square size={16} />
                        {zones.length > 0 ? 'Draw Another Area' : 'Draw Area'}
                      </button>
                    )}
                  </>
                ) : (
                  <>
                    <select
                      value={currentZoneType}
                      onChange={(e) => setCurrentZoneType(e.target.value)}
                      className="text-sm border rounded-lg px-3 py-2"
                    >
                      {Object.entries(ZONE_TYPES).map(([key, val]) => (
                        <option key={key} value={key}>{val.icon} {val.label}</option>
                      ))}
                    </select>
                    <button
                      onClick={() => startDrawing('polygon')}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 flex items-center justify-center gap-2"
                    >
                      <Square size={16} />
                      Draw Area
                    </button>
                    {isLinearZone(currentZoneType) && (
                      <button
                        onClick={() => startDrawing('polyline')}
                        className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 flex items-center justify-center gap-2"
                      >
                        <Ruler size={16} />
                        Draw Line
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>
          )}

          {/* Drawing mode indicator */}
          {drawingMode && (
            <div className="absolute bottom-4 left-2 right-2 md:left-1/2 md:right-auto md:-translate-x-1/2 z-10">
              <div className="bg-yellow-100 border border-yellow-400 rounded-lg px-3 md:px-4 py-2 flex items-center justify-between md:justify-start gap-2 md:gap-3">
                <span className="text-xs md:text-sm text-yellow-800">
                  Tap to draw {drawingMode === 'polygon' ? 'an area' : 'a line'}.{' '}
                  <span className="hidden md:inline">Double-click</span>
                  <span className="md:hidden">Double-tap</span> to finish.
                </span>
                <button
                  onClick={cancelDrawing}
                  className="text-yellow-800 hover:text-yellow-900 font-medium text-xs md:text-sm whitespace-nowrap"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          <div ref={mapRef} className="w-full h-full" style={{ minHeight: '400px' }} />

          {!mapLoaded && (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-200">
              <div className="text-center">
                <div className="animate-spin text-4xl mb-2">🌍</div>
                <p className="text-gray-600">Loading map...</p>
              </div>
            </div>
          )}
        </div>

        {/* Zones panel */}
        {!quickMode && (
          <div className={`
            ${showZonePanel ? '' : 'hidden md:block md:w-0 md:overflow-hidden'}
            md:w-80 bg-white border-t md:border-t-0 md:border-l flex flex-col transition-all
            ${showZonePanel ? 'max-h-[50vh] md:max-h-none md:h-auto' : ''}
            order-last
          `}>
            <div
              className="p-3 md:p-4 border-b bg-gray-50 cursor-pointer md:cursor-default"
              onClick={() => window.innerWidth < 768 && setShowZonePanel(!showZonePanel)}
            >
              <div className="flex items-center justify-between mb-2 md:mb-3">
                <h3 className="font-semibold text-gray-900 text-sm md:text-base">Zones ({zones.length})</h3>
                <button
                  type="button"
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); setShowZonePanel(!showZonePanel); }}
                  className="p-1 hover:bg-gray-200 rounded md:hidden"
                >
                  <ChevronDown size={18} className={`transition-transform ${showZonePanel ? 'rotate-180' : ''}`} />
                </button>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-2 gap-2 md:gap-3">
                <div className="bg-white rounded-lg p-2 text-center">
                  <div className="text-base md:text-lg font-bold text-blue-600">{formatSqFt(totalSqFt)}</div>
                  <div className="text-xs text-gray-500">Total Area</div>
                </div>
                {totalLinearFt > 0 && (
                  <div className="bg-white rounded-lg p-2 text-center">
                    <div className="text-base md:text-lg font-bold text-green-600">{formatLinearFt(totalLinearFt)}</div>
                    <div className="text-xs text-gray-500">Linear Ft</div>
                  </div>
                )}
                {totalEstimate > 0 && (
                  <div className="bg-white rounded-lg p-2 text-center col-span-2">
                    <div className="text-lg md:text-xl font-bold text-green-600">
                      ${totalEstimate.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </div>
                    <div className="text-xs text-gray-500">Estimated Quote</div>
                  </div>
                )}
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-2">
              {zones.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Square size={40} className="mx-auto mb-2 opacity-30" />
                  <p className="text-sm">No zones drawn yet</p>
                  <p className="text-xs mt-1">Select a zone type and click "Draw Area"</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {zones.map((zone, index) => (
                    <div
                      key={index}
                      onClick={() => setSelectedZoneIndex(index)}
                      className={`p-3 rounded-lg border cursor-pointer transition-all ${
                        selectedZoneIndex === index ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <div className="w-4 h-4 rounded" style={{ backgroundColor: zone.color }} />
                          <span className="font-medium text-sm text-gray-900">
                            {zone.name || `${zone.zoneType} ${index + 1}`}
                          </span>
                        </div>
                        {!readOnly && (
                          <button
                            onClick={(e) => { e.stopPropagation(); deleteZone(index); }}
                            className="p-1 text-gray-400 hover:text-red-500"
                          >
                            <Trash2 size={14} />
                          </button>
                        )}
                      </div>
                      <div className="text-sm text-gray-600 mb-2">
                        {zone.squareFeet > 0 && <span className="mr-3">{formatSqFt(zone.squareFeet)}</span>}
                        {zone.linearFeet > 0 && <span>{formatLinearFt(zone.linearFeet)}</span>}
                      </div>
                      {serviceTypes.length > 0 && (
                        <div className="mt-2">
                          <select
                            value={zone.serviceTypeId || ''}
                            onChange={(e) => updateZoneService(index, parseInt(e.target.value) || null)}
                            onClick={(e) => e.stopPropagation()}
                            disabled={readOnly}
                            className="w-full text-xs border rounded px-2 py-1.5 disabled:bg-gray-100"
                          >
                            <option value="">Select service for pricing...</option>
                            {serviceTypes.map(st => (
                              <option key={st.id} value={st.id}>{st.name}</option>
                            ))}
                          </select>
                        </div>
                      )}
                      {zone.calculatedPrice > 0 && (
                        <div className="mt-2 flex items-center gap-1 text-green-600 font-medium text-sm">
                          <DollarSign size={14} />
                          {zone.calculatedPrice.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SatelliteMeasure;